/**
 * VerifyAcceptancePage - Verificação pública de comprovante de aceite
 *
 * O visitante informa o código impresso no PDF e seleciona o arquivo; o SHA-256 é
 * calculado no próprio navegador (o PDF não é enviado). A resposta é mínima:
 * situação, data, finalidade, versão do termo e nomes mascarados.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-27 v1.1.0 - Preenche o código recebido pelo QR do comprovante.
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 4).
 */
import { useState } from 'react';
import { CheckCircle2, FileSearch, Loader2, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Result = { valid: boolean; accepted_at?: string; purpose?: string; terms_version?: number; signers?: string[]; final_hash?: string };
const PURPOSE: Record<string, string> = { acknowledgement: 'Ciência', declaration: 'Declaração', approval: 'Aprovação', authorization: 'Autorização' };

async function sha256File(f: File) {
  const d = await crypto.subtle.digest('SHA-256', await f.arrayBuffer());
  return Array.from(new Uint8Array(d)).map((x) => x.toString(16).padStart(2, '0')).join('');
}

export default function VerifyAcceptancePage() {
  // O QR transporta somente o código público; o PDF continua obrigatório para validar o hash.
  const [code, setCode] = useState(() => {
    const queryCode = new URLSearchParams(window.location.search).get('codigo') ?? '';
    return queryCode.replace(/[^A-Za-z0-9-]/g, '').slice(0, 30).toUpperCase();
  });
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState('');

  const verify = async () => {
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const hash = await sha256File(file);
      const { data, error: e } = await (supabase.rpc as any)('verify_acceptance_receipt', {
        p_code: code.replace(/[^A-Za-z0-9-]/g, '').slice(0, 40), p_pdf_sha256: hash,
      });
      if (e) throw e;
      setResult(data as Result);
    } catch (e) {
      setError((e as Error).message || 'Não foi possível verificar agora.');
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 space-y-5 shadow-sm">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-lg font-semibold"><FileSearch className="h-5 w-5 text-primary" />Verificar aceite</h1>
          <p className="text-xs text-muted-foreground">Informe o código do comprovante e selecione o PDF original. O arquivo é conferido no seu navegador e não é enviado.</p>
        </div>
        <div className="space-y-1.5"><Label>Código de verificação</Label>
          <Input value={code} maxLength={30} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" onChange={(e) => setCode(e.target.value.toUpperCase())} /></div>
        <div className="space-y-1.5"><Label>Arquivo PDF</Label>
          <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></div>
        <Button className="w-full" disabled={loading || code.replace(/[^A-Z0-9]/g, '').length < 16 || !file} onClick={verify}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verificar
        </Button>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {result && (result.valid ? (
          <div className="border-l-2 border-success pl-3 space-y-1 text-sm">
            <p className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4 text-success" />Comprovante íntegro</p>
            <p className="text-xs">Finalidade: {PURPOSE[result.purpose ?? ''] ?? result.purpose} · Termo v{result.terms_version}</p>
            <p className="text-xs">Aceito em: {result.accepted_at ? new Date(result.accepted_at).toLocaleString('pt-BR') : '—'}</p>
            <p className="text-xs">Participantes: {(result.signers ?? []).join(', ')}</p>
            <p className="break-all text-[10px] text-muted-foreground">SHA-256: {result.final_hash}</p>
          </div>
        ) : (
          <div className="border-l-2 border-destructive pl-3 text-sm">
            <p className="flex items-center gap-1.5 font-semibold"><XCircle className="h-4 w-4 text-destructive" />Não foi possível confirmar</p>
            <p className="text-xs text-muted-foreground">Código inválido ou o arquivo foi alterado.</p>
          </div>
        ))}
      </div>
    </main>
  );
}
